import AddIcon from '@mui/icons-material/Add'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { get, noop, sortBy } from 'lodash'
import lowerCase from 'lodash/lowerCase'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useState } from 'react'
import { FormProvider, useFieldArray, useForm } from 'react-hook-form'

import { useGetGeneralFeeListMutation } from 'api/pricing/getGeneralFeeList'
import { useCreateUpdateDraftFeeMutation } from 'api/work-order/createUpdateDraftFee'
import BillingLockedTooltip from 'components/billing/common/lock-billing-date/BillingLockedTooltip'
import CancelButton from 'components/buttons/CancelButton'
import SaveButton from 'components/buttons/SaveButton'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHConfirmDeleteButton from 'components/common/HHConfirmDeleteButton'
import HHDialogActions from 'components/common/HHDialogActions'
import HHDialogTitle from 'components/common/HHDialogTitle'
import HHDisplayMoney from 'components/common/HHDisplayMoney'
import { buildWorkOrderGeneralFeeFilter } from 'components/work-order/details/fees/filters'
import GeneralFeeFormRow from 'components/work-order/WorkOrderReviewDialog/GeneralFeesDialog/GeneralFeeFormRow'
import T from 'T'
import { isPriceTypeDollar } from 'utils/service'

const GeneralFeesDialog = ({ isLocked = false, open, woId, fees, onClose = noop, onRefresh = noop }) => {
  const [generalFeeList, setGeneralFeeList] = useState([])
  const [generalFeesById, setGeneralFeesById] = useState({})
  const [toBeDeleted, setToBeDeleted] = useState([])
  const [createUpdateDraftFee, { isLoading: isCreateUpdateFeeLoading }] = useCreateUpdateDraftFeeMutation()

  const [total, setTotal] = useState(0)
  const methods = useForm({
    defaultValues: {
      fees: [
        {
          feeTaxId: '',
          feeCharge: '',
          chargeType: '',
          subChargeType: '',
          measure: '',
          quantity: 1,
          invoiceLineItemId: '',
          workOrderId: '',
        },
      ],
    },
  })
  const { control, watch, reset, setValue, getValues, handleSubmit } = methods
  const [getGeneralFeeList] = useGetGeneralFeeListMutation()
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'fees',
  })
  const formFees = watch('fees')

  const addFee = useCallback(() => {
    append({
      feeTaxId: '',
      feeCharge: '',
      chargeType: '',
      subChargeType: '',
      measure: '',
      quantity: 1,
      invoiceLineItemId: '',
      workOrderId: woId,
    })
  }, [append, woId])

  const updateGeneralFees = payload => {
    const feeAndCharges = get(payload, 'feeAndCharges', []).filter(fee => fee?.feeName && isPriceTypeDollar(fee?.valueType))
    const newFeesMapById = {}
    feeAndCharges.forEach(item => {
      newFeesMapById[item.id] = item
    })
    const sortedFeeAndCharges = sortBy(feeAndCharges, fee => lowerCase(get(fee, 'feeName', '')))
    setGeneralFeeList(sortedFeeAndCharges)
    setGeneralFeesById(newFeesMapById)
  }

  const initializeFormData = () => {
    if (fees.length === 0) {
      reset({
        fees: [
          {
            feeTaxId: '',
            feeCharge: '',
            chargeType: '',
            subChargeType: '',
            measure: '',
            quantity: 1,
            invoiceLineItemId: '',
            workOrderId: woId,
          },
        ],
      })
    } else {
      const initialFees = fees.map(fee => {
        const { feeTaxId } = fee
        const selectedFee = generalFeesById[feeTaxId]
        const feeCharge = get(fee, 'feeCharge', 0)
        const subChargeType = get(selectedFee, 'subChargeType', 'General')
        const feeName = get(selectedFee, 'feeName', '')
        return {
          ...fee,
          workOrderId: woId,
          feeName,
          feeTaxId,
          feeCharge,
          subChargeType,
          isEdit: 'true',
        }
      })
      reset({
        fees: initialFees,
      })
    }
  }

  const handleFeeChange = useCallback(
    ({ feeId, index }) => {
      const selectedFee = generalFeesById[feeId]
      const value = get(selectedFee, 'value', 0)
      setValue(`fees.${index}.feeCharge`, value.toFixed(2))
    },
    [generalFeesById, setValue]
  )

  const handleRemove = useCallback(
    index => {
      if (formFees.length === 1) {
        reset({
          fees: [
            {
              feeTaxId: '',
              feeCharge: '',
              chargeType: '',
              subChargeType: '',
              measure: '',
              quantity: 1,
              invoiceLineItemId: '',
              workOrderId: woId,
            },
          ],
        })
      } else {
        const toBeDeletedItem = getValues(`fees.${index}`)
        if (toBeDeletedItem?.id) {
          setToBeDeleted([...toBeDeleted, { ...toBeDeletedItem, delete: true }])
        }
        remove(index)
      }
    },
    [formFees, getValues, remove, reset, toBeDeleted, woId]
  )

  const onSubmit = async data => {
    await createUpdateDraftFee({
      draftFees: data.fees,
      removeDraftFees: toBeDeleted,
    }).unwrap()
    onRefresh()
    onClose()
  }

  const handleDeleteConfirmed = useCallback(async () => {
    const removeDraftFees = fees.filter(({ id }) => id).map(fee => ({ ...fee, delete: true }))
    await createUpdateDraftFee({
      draftFees: [],
      removeDraftFees,
    }).unwrap()
    onRefresh()
    onClose()
  }, [createUpdateDraftFee, fees, onClose, onRefresh])

  useEffect(() => {
    if (woId && open) {
      getGeneralFeeList(buildWorkOrderGeneralFeeFilter(woId))
        .unwrap()
        .then(payload => {
          updateGeneralFees(payload)
          initializeFormData()
        })
        .catch(() => {
          setGeneralFeeList([])
          setGeneralFeesById({})
        })
    }
    if (!open) {
      reset({
        fees: [
          {
            feeTaxId: '',
            feeCharge: '',
            chargeType: '',
            subChargeType: '',
            measure: '',
            quantity: 1,
            invoiceLineItemId: '',
            workOrderId: woId,
          },
        ],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initializeFormData reads the fee list this effect loads; listing it re-fires the fetch
  }, [woId, open, fees])

  useEffect(() => {
    const subscription = watch(value => {
      const totalCharge = value.fees.reduce((accumulator, currentFee) => {
        const itemCharge = currentFee.quantity * currentFee.feeCharge
        return accumulator + itemCharge
      }, 0)
      setTotal(totalCharge)
    })
    return () => subscription.unsubscribe()
  }, [watch])

  return (
    <HHBaseDialog maxWidth="sm" open={open} onClose={onClose}>
      <HHDialogTitle sx={{ fontWeight: 600 }} title={T.FEES} onClose={onClose} />
      <DialogContent sx={{ mt: 2 }}>
        <Grid container rowSpacing={2}>
          <FormProvider {...methods}>
            {fields.map((field, index) => {
              return (
                <GeneralFeeFormRow
                  key={field.id}
                  field={field}
                  generalFeeList={generalFeeList}
                  index={index}
                  watch={watch}
                  onFeeChange={handleFeeChange}
                  onRemove={handleRemove}
                />
              )
            })}
          </FormProvider>
          <Grid size={12}>
            <Divider />
          </Grid>
          <Grid size={12}>
            <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={addFee}>
              {T.FEE}
            </Button>
          </Grid>
          <Grid size={12}>
            <Divider />
          </Grid>
          <Grid container columnSpacing={1} size={12} wrap="nowrap">
            <Grid size="grow">
              <Box alignItems="center" display="flex" height="100%">
                <Typography fontWeight={600} variant="h4">
                  {T.TOTAL}
                </Typography>
              </Box>
            </Grid>
            <Grid>
              <Box width="20px" />
            </Grid>
            <Grid size="grow" />
            <Grid>
              <Box width="20px" />
            </Grid>
            <Grid size="grow">
              <Typography align="right" fontWeight={600} variant="h4">
                <HHDisplayMoney formatToDollars={false} value={total} />
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <HHDialogActions>
        {fees?.length > 0 && (
          <BillingLockedTooltip isLocked={isLocked} placement="bottom">
            <HHConfirmDeleteButton
              confirmDescription="Are you sure you want to delete these fees"
              confirmTitle="Delete fee"
              disabled={isLocked}
              size="small"
              onDeleteConfirmed={handleDeleteConfirmed}
            />
          </BillingLockedTooltip>
        )}
        <Box sx={{ flex: '1 1 auto' }} />
        <Stack direction="row" spacing={1}>
          <CancelButton onClick={onClose} />
          <BillingLockedTooltip isLocked={isLocked} placement="bottom">
            <SaveButton disabled={isLocked} loading={isCreateUpdateFeeLoading} onClick={handleSubmit(onSubmit)} />
          </BillingLockedTooltip>
        </Stack>
      </HHDialogActions>
    </HHBaseDialog>
  )
}

GeneralFeesDialog.propTypes = {
  isLocked: PropTypes.bool,
  open: PropTypes.bool,
  woId: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  onRefresh: PropTypes.func,
  fees: PropTypes.array,
}

export default GeneralFeesDialog
